import { useEffect, useMemo, useState } from 'react';
import { format, formatISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { formatCurrency, formatShortDateWithoutTimezone, isValidCpf } from '@ibk/shared-utils';
import { BankName, PixType, TransactionFlow } from '../../enums';
import { toPixType } from '../../utils/pixKeyUtils';
import { useGetAccountPayablePixKey } from '../useGetAccountPayablePixKey';
import { useGetFinancialInstitutions } from '../useGetFinancialInstitutions';
import { usePostOrPatchPaymentDetails } from '../usePostOrPatchPaymentDetails';
import { usePostPix } from '../usePostPix';
import { usePostValidateDuplicate } from '../usePostValidateDuplicate';
const STEP_BANK_DETAILS = 0;
const STEP_REVISION = 1;
const STEP_SUCCESS = 2;
export function usePixAccountPayable(accountPayable) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const { maskDocumentHidden, isValidCnpj } = useCnpjAlphanumeric();
    const { postValidateDuplicate, isDuplicate, loading: loadingValidateDuplicate } = usePostValidateDuplicate();
    const { getAccountPayablePixKey, getAccountPayablePixKeyByPaymentDetails, accountPayablePixKey, error: errorGetAccountPayablePixKey, loading: loadingGetAccountPayablePixKey, } = useGetAccountPayablePixKey(accountPayable);
    const { postOrPatchPaymentDetails, paymentDetails, error: errorPostOrPatchPaymentDetails, loading: loadingPostOrPatchPaymentDetails, } = usePostOrPatchPaymentDetails();
    const { postPix, pix, pixAgencyAccount, pixScheduled, pixError, pixAgencyAccountError, pixScheduledError, loading: loadingPostPix, } = usePostPix();
    const { financialInstitutions, getFinancialInstitutions } = useGetFinancialInstitutions();
    const [step, setStep] = useState(accountPayable?.isEmployeePayment ? STEP_BANK_DETAILS : STEP_REVISION);
    const [transactionStartAt, setTransactionStartAt] = useState(new Date().toISOString());
    const [scheduleDate, setScheduleDate] = useState(undefined);
    const [showPaymentDataNotFoundError, setShowPaymentDataNotFoundError] = useState(false);
    const [fetchingPaymentDataLoading, setFetchingPaymentDataLoading] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showPinKeyboard, setShowPinKeyboard] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [showAlert, setShowAlert] = useState(undefined);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [confirmDuplicate, setConfirmDuplicate] = useState(false);
    const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
    const [showPixTimeoutError, setShowPixTimeoutError] = useState(false);
    const [showPixLimitExceededModal, setShowPixLimitExceededModal] = useState(false);
    useEffect(() => {
        if (accountPayable) {
            setFetchingPaymentDataLoading(true);
            getAccountPayablePixKey();
        }
        else {
            setFetchingPaymentDataLoading(false);
            setShowPaymentDataNotFoundError(true);
        }
    }, [accountPayable]);
    useEffect(() => {
        if (paymentDetails) {
            getAccountPayablePixKeyByPaymentDetails(paymentDetails);
        }
    }, [paymentDetails]);
    useEffect(() => {
        if (errorPostOrPatchPaymentDetails) {
            setShowAlert({
                message: errorPostOrPatchPaymentDetails,
            });
        }
    }, [errorPostOrPatchPaymentDetails]);
    useEffect(() => {
        if (accountPayablePixKey) {
            if (paymentDetails) {
                setStep(STEP_REVISION);
            }
            if (accountPayablePixKey?.bankCode) {
                getFinancialInstitutions();
            }
            setFetchingPaymentDataLoading(false);
        }
    }, [accountPayablePixKey]);
    useEffect(() => {
        if (errorGetAccountPayablePixKey) {
            if (accountPayable?.isEmployeePayment) {
                setShowAlert({
                    message: errorGetAccountPayablePixKey,
                });
                setFetchingPaymentDataLoading(false);
            }
            else {
                setShowPaymentDataNotFoundError(true);
                setFetchingPaymentDataLoading(false);
            }
        }
    }, [errorGetAccountPayablePixKey]);
    useEffect(() => {
        if (pix) {
            setShowPinKeyboard(false);
            setStep(STEP_SUCCESS);
        }
    }, [pix]);
    useEffect(() => {
        if (pixAgencyAccount) {
            setShowPinKeyboard(false);
            setStep(STEP_SUCCESS);
        }
    }, [pixAgencyAccount]);
    useEffect(() => {
        if (pixScheduled) {
            setShowPinKeyboard(false);
            setStep(STEP_SUCCESS);
        }
    }, [pixScheduled]);
    useEffect(() => {
        if (pixError) {
            onError(pixError);
        }
    }, [pixError]);
    useEffect(() => {
        if (pixAgencyAccountError) {
            onError(pixAgencyAccountError);
        }
    }, [pixAgencyAccountError]);
    useEffect(() => {
        if (pixScheduledError) {
            onError(pixScheduledError);
        }
    }, [pixScheduledError]);
    useEffect(() => {
        if (isDuplicate === true) {
            setShowDuplicateModal(true);
        }
        else if (isDuplicate === false) {
            setShowPinKeyboard(true);
        }
    }, [isDuplicate]);
    const key = useMemo(() => {
        return accountPayablePixKey?.key;
    }, [accountPayablePixKey]);
    const keyType = useMemo(() => {
        return toPixType(accountPayablePixKey?.type);
    }, [accountPayablePixKey]);
    const accountType = useMemo(() => {
        return accountPayablePixKey?.accountTypeAcronym;
    }, [accountPayablePixKey]);
    const accountNumber = useMemo(() => {
        return accountPayablePixKey?.account;
    }, [accountPayablePixKey]);
    const agencyNumber = useMemo(() => {
        return accountPayablePixKey?.branch;
    }, [accountPayablePixKey]);
    const bank = useMemo(() => {
        if (accountPayablePixKey?.bankCode && financialInstitutions) {
            return financialInstitutions?.find(institution => institution.code === accountPayablePixKey?.bankCode);
        }
    }, [financialInstitutions, accountPayablePixKey]);
    const loading = useMemo(() => {
        return (loadingGetAccountPayablePixKey || loadingPostOrPatchPaymentDetails || loadingPostPix || loadingValidateDuplicate);
    }, [loadingGetAccountPayablePixKey, loadingPostOrPatchPaymentDetails, loadingPostPix, loadingValidateDuplicate]);
    const isEmployeePayment = useMemo(() => accountPayable?.isEmployeePayment, [accountPayable]);
    const isEmployeePaymentAndPaymentDataNotFoundError = useMemo(() => {
        return showPaymentDataNotFoundError;
    }, [isEmployeePayment, showPaymentDataNotFoundError]);
    const isTimeoutError = useMemo(() => {
        return showPixTimeoutError && !loading && !showPaymentDataNotFoundError;
    }, [showPixTimeoutError]);
    const isNotFetchingPaymentDataLoadingAndPaymentDataNotFoundError = useMemo(() => {
        return !fetchingPaymentDataLoading && !showPaymentDataNotFoundError && !isTimeoutError;
    }, [fetchingPaymentDataLoading, showPaymentDataNotFoundError]);
    const showStepperHorizontal = useMemo(() => {
        return isNotFetchingPaymentDataLoadingAndPaymentDataNotFoundError && step !== STEP_SUCCESS && !showPixTimeoutError;
    }, [isNotFetchingPaymentDataLoadingAndPaymentDataNotFoundError, step]);
    const showBankDetailsStep = useMemo(() => {
        return step === STEP_BANK_DETAILS && !loading;
    }, [isNotFetchingPaymentDataLoadingAndPaymentDataNotFoundError, step, loading]);
    const showRevisionStep = useMemo(() => {
        return step === STEP_REVISION && !loading;
    }, [isNotFetchingPaymentDataLoadingAndPaymentDataNotFoundError, step, loading]);
    const showSuccessStep = useMemo(() => {
        return step === STEP_SUCCESS && !loading;
    }, [step, loading]);
    const amount = useMemo(() => formatCurrency(accountPayable?.amount ?? 0), [accountPayable]);
    const dueDate = useMemo(() => accountPayable?.dueDate && formatShortDateWithoutTimezone(accountPayable?.dueDate), [accountPayable]);
    const receiverName = useMemo(() => {
        if (accountPayablePixKey || pix || pixAgencyAccount) {
            return (pix?.transaction?.details?.counterparty?.name ??
                pixAgencyAccount?.transaction?.details?.counterparty?.name ??
                accountPayablePixKey?.receiverName ??
                accountPayable?.title);
        }
    }, [accountPayablePixKey]);
    const receiverDocumentType = useMemo(() => {
        if (accountPayablePixKey || pix || pixAgencyAccount) {
            const document = pix?.transaction?.details?.counterparty?.document ??
                pixAgencyAccount?.transaction?.details?.counterparty?.document ??
                accountPayablePixKey?.receiverDocumentNumber ??
                accountPayable?.employeeDetails?.documentNumber;
            if (document) {
                if (isValidCpf(document)) {
                    return t('cpfTwoPoints');
                }
                return isValidCnpj(document) ? t('cnpjTwoPoints') : t('cpfTwoPoints');
            }
        }
    }, [isValidCnpj]);
    const receiverDocument = useMemo(() => {
        if (accountPayablePixKey || pix || pixAgencyAccount) {
            const document = pix?.transaction?.details?.counterparty?.document ??
                pixAgencyAccount?.transaction?.details?.counterparty?.document ??
                accountPayablePixKey?.receiverDocumentNumber ??
                accountPayable?.employeeDetails?.documentNumber;
            if (document) {
                return maskDocumentHidden(document);
            }
        }
    }, [accountPayablePixKey, maskDocumentHidden]);
    const receiverBankName = useMemo(() => {
        if (accountPayablePixKey || pix || pixAgencyAccount) {
            return (pix?.transaction?.details?.counterparty?.bank?.name ??
                pixAgencyAccount?.transaction?.details?.counterparty?.bank?.name ??
                accountPayablePixKey?.bankName);
        }
    }, [accountPayablePixKey]);
    const originName = useMemo(() => {
        return (getSelectedAccountInfo()?.companyName ??
            `${getSelectedAccountInfo()?.firstName} ${getSelectedAccountInfo()?.lastName}`);
    }, []);
    const originDocumentType = useMemo(() => {
        const documentNumber = getSelectedAccountInfo()?.documentNumber;
        if (documentNumber) {
            if (isValidCpf(documentNumber)) {
                return t('cpfTwoPoints');
            }
            return isValidCnpj(documentNumber) ? t('cnpjTwoPoints') : t('cpfTwoPoints');
        }
    }, [isValidCnpj]);
    const originDocument = useMemo(() => {
        const documentNumber = getSelectedAccountInfo()?.documentNumber;
        if (documentNumber) {
            return maskDocumentHidden(documentNumber);
        }
    }, [maskDocumentHidden]);
    const originBankName = useMemo(() => {
        return BankName.CELCOIN;
    }, []);
    const paymentDate = useMemo(() => {
        return scheduleDate ? formatShortDateWithoutTimezone(scheduleDate) : format(new Date(), 'dd/MM/yyyy');
    }, [scheduleDate]);
    const transactionDate = useMemo(() => {
        return format(new Date(), 'dd/MM/yyyy HH:mm');
    }, [pix, pixAgencyAccount, pixScheduled]);
    const transactionId = useMemo(() => {
        if (pix) {
            return pix.transaction?.transactionId;
        }
        if (pixAgencyAccount) {
            return pixAgencyAccount.transaction?.transactionId;
        }
        if (pixScheduled) {
            return pixScheduled?.instructionScheduleId;
        }
    }, [pix, pixAgencyAccount, pixScheduled]);
    const transactionFlow = useMemo(() => {
        if (pixScheduled) {
            return TransactionFlow.SCHEDULED;
        }
        return TransactionFlow.CASH_OUT;
    }, []);
    const isPendingTransaction = useMemo(() => {
        const isWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAYMENTS_OPERATOR_REQUEST_PAYMENT, undefined, cache?.data);
        if (pix && isWhiteListed) {
            return !pix.transaction?.transactionId;
        }
        if (pixAgencyAccount && isWhiteListed) {
            return !pixAgencyAccount.transaction?.transactionId;
        }
        if (pixScheduled && isWhiteListed) {
            return pixScheduled?.isApproved === false;
        }
        return false;
    }, [pix, pixAgencyAccount, pixScheduled]);
    const successStepTitle = useMemo(() => {
        if (isPendingTransaction) {
            if (scheduleDate) {
                return t('pending-schedule');
            }
            return t('pending-payment');
        }
        return t('all-very-well');
    }, [isPendingTransaction, scheduleDate]);
    const successStepCaption = useMemo(() => {
        if (isPendingTransaction) {
            if (scheduleDate) {
                return t('your-schedule-is-pending-approval');
            }
            return t('your-payment-is-pending-approval');
        }
        if (scheduleDate) {
            return t('transfer-scheduled-with-success');
        }
        return t('transfer-done-with-success');
    }, [isPendingTransaction, scheduleDate]);
    const successStepBackButtonLabel = useMemo(() => {
        if (isPendingTransaction) {
            return t('go-back-to-payments');
        }
        return t('show-receipt');
    }, [isPendingTransaction]);
    function onError(error) {
        setShowPinKeyboard(false);
        if (error === t('pix-limit-exceeded')) {
            setShowPixLimitExceededModal(true);
        }
        else if (error === t('pix-timeout-error')) {
            setShowPixTimeoutError(true);
        }
        else if (error === t('insufficient-balance-modal-title')) {
            setShowInsufficientBalanceModal(true);
        }
        else {
            setShowAlert({
                message: error,
            });
        }
    }
    function onPressBackOnBankDetailsStep() {
        navigate(-1);
    }
    function onPressNextOnBankDetailsStep(key, keyType, accountNumber, agencyNumber, accountType, financialInstitutionCode) {
        const sanitizedAccountNumber = accountNumber?.replace(/\D/g, '');
        const hasNotEmployeePaymentDetails = !accountPayable?.employeePaymentDetails;
        const hasNoPaymentDetails = !paymentDetails;
        const hasNoPaymentDetailsAndEmployeePaymentDetails = hasNoPaymentDetails && hasNotEmployeePaymentDetails;
        const isBankAccountKey = keyType === PixType.AGENCY;
        const isDifferentBankAccountKey = accountPayablePixKey &&
            isBankAccountKey &&
            (sanitizedAccountNumber !== accountPayablePixKey?.account ||
                agencyNumber !== accountPayablePixKey?.branch ||
                accountType !== accountPayablePixKey?.accountTypeAcronym ||
                financialInstitutionCode !== accountPayablePixKey?.bankCode);
        const isDifferentPixKey = accountPayablePixKey && !isBankAccountKey && key !== accountPayablePixKey?.key;
        if (!accountPayablePixKey ||
            isDifferentBankAccountKey ||
            isDifferentPixKey ||
            hasNoPaymentDetailsAndEmployeePaymentDetails) {
            postOrPatchPaymentDetails({
                keyType,
                pixKey: key,
                id: paymentDetails?.id ?? accountPayable?.employeePaymentDetails,
                document: paymentDetails?.documentNumber ?? accountPayable?.employeeDetails?.documentNumber,
                documentType: paymentDetails?.documentType ?? accountPayable?.employeeDetails?.documentType,
                accountType,
                financialInstitutionCode,
                agencyNumber,
                accountNumber: sanitizedAccountNumber,
                party: paymentDetails?.party ?? accountPayable?.party,
            });
        }
        else {
            setStep(STEP_REVISION);
        }
    }
    function onPressBackOnRevisionStep() {
        if (accountPayable?.isEmployeePayment) {
            setStep(STEP_BANK_DETAILS);
        }
        else {
            navigate(-1);
        }
    }
    function onPressNextOnRevisionStep() {
        const isWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAGAMENTOS_DUPLICATE_PIX, undefined, cache?.data);
        if (isWhiteListed && !scheduleDate) {
            postValidateDuplicate({
                date: formatISO(new Date()),
                key: accountPayablePixKey?.key,
                keyType: accountPayablePixKey?.type,
                amount: accountPayable?.amount,
                receiverName: accountPayablePixKey?.receiverName,
                counterpartyBankAccount: accountPayablePixKey?.account?.replace(/\D/g, ''),
                counterpartyBankBranch: accountPayablePixKey?.branch,
                counterpartyBankCode: accountPayablePixKey?.bankCode,
            });
        }
        else {
            setShowPinKeyboard(true);
        }
    }
    function onPressConfirmOnPinKeyBoard(credential) {
        postPix(credential, transactionStartAt, scheduleDate, { ...accountPayablePixKey, confirmDuplicate });
    }
    function onPressBackOnSuccessStep() {
        if (isPendingTransaction) {
            navigate('/pagamentos');
        }
        else {
            setShowReceipt(true);
        }
    }
    function onPressNextOnSuccessStep() {
        navigate('/pagamentos');
    }
    function onConfirmDuplicateOnRevisionStep() {
        setConfirmDuplicate(true);
        setShowPinKeyboard(true);
    }
    function onCloseDuplicateModalOnRevisionStep() {
        setShowDuplicateModal(false);
    }
    return {
        isTimeoutError,
        isEmployeePaymentAndPaymentDataNotFoundError,
        isNotFetchingPaymentDataLoadingAndPaymentDataNotFoundError,
        showStepperHorizontal,
        showBankDetailsStep,
        showRevisionStep,
        showSuccessStep,
        transactionId,
        transactionFlow,
        amount,
        receiverName,
        receiverDocument,
        receiverDocumentType,
        receiverBankName,
        originName,
        originDocument,
        originDocumentType,
        originBankName,
        dueDate,
        paymentDate,
        transactionDate,
        loading,
        isPendingTransaction,
        successStepTitle,
        successStepCaption,
        successStepBackButtonLabel,
        scheduleDate,
        key,
        keyType,
        accountType,
        accountNumber,
        agencyNumber,
        bank,
        setScheduleDate,
        step,
        setStep,
        showPaymentDataNotFoundError,
        setShowPaymentDataNotFoundError,
        fetchingPaymentDataLoading,
        setFetchingPaymentDataLoading,
        showCalendar,
        setShowCalendar,
        showPinKeyboard,
        setShowPinKeyboard,
        showReceipt,
        setShowReceipt,
        showAlert,
        setShowAlert,
        transactionStartAt,
        setTransactionStartAt,
        setShowDuplicateModal,
        showDuplicateModal,
        showPixTimeoutError,
        setShowPixTimeoutError,
        confirmDuplicate,
        setConfirmDuplicate,
        showInsufficientBalanceModal,
        setShowInsufficientBalanceModal,
        showPixLimitExceededModal,
        setShowPixLimitExceededModal,
        onConfirmDuplicateOnRevisionStep,
        onCloseDuplicateModalOnRevisionStep,
        onPressBackOnBankDetailsStep,
        onPressNextOnBankDetailsStep,
        onPressBackOnRevisionStep,
        onPressNextOnRevisionStep,
        onPressBackOnSuccessStep,
        onPressNextOnSuccessStep,
        onPressConfirmOnPinKeyBoard,
    };
}
